/**
 * @module organizationLambda
 * @description Provides functions to interact with organization-related API endpoints.
 * Utilizes axios-based BaseApiService for HTTP requests.
 */

import { apiGet, apiPost, validateRequiredParams } from '@/services/BaseApiService';

const baseUrl = '/organization/';

/**
 * Retrieves an organization's details by its ID.
 * @param {string} id - The unique identifier of the organization.
 * @returns {Promise<Object>} Promise resolving to the organization's details.
 * @throws {Error} If the `id` parameter is missing or retrieval fails.
 */
export const getOrganization = async (id) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getorganization`, { id });
};

/**
 * Retrieves the parent organizations for a given organization ID.
 * @param {string} id - The unique identifier of the organization.
 * @returns {Promise<Object>} Promise resolving to the parent organizations.
 * @throws {Error} If the `id` parameter is missing or retrieval fails.
 */
export const getOrganizationParents = async (id) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getorganizationparents`, { id });
};

/**
 * Retrieves the affiliates of an organization by its ID.
 * @param {string} id - The unique identifier of the organization.
 * @returns {Promise<Object>} Promise resolving to the organization's affiliates.
 * @throws {Error} If the `id` parameter is missing or retrieval fails.
 */
export const getAffiliates = async (id) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getaffiliates`, { id });
};

/**
 * Inserts a new organization.
 * @param {Object} organization - The organization object to insert.
 * @returns {Promise<Object>} Promise resolving to the response data.
 * @throws {Error} If the `organization` parameter is missing or insertion fails.
 */
export const insertOrganization = async (organization) => {
  validateRequiredParams({ organization });
  return apiPost(`${baseUrl}insertorganization`, organization);
};

/**
 * Updates an existing organization.
 * @param {Object} organization - The organization object to update.
 * @returns {Promise<Object>} Promise resolving to the response data.
 * @throws {Error} If the `organization` parameter is missing or update fails.
 */
export const updateOrganization = async (organization) => {
  validateRequiredParams({ organization });
  return apiPost(`${baseUrl}updateorganization`, organization);
};

/**
 * Retrieves the contacts of an organization by its ID.
 * @param {string} id - The unique identifier of the organization.
 * @param {boolean} [activeOnly] - If true, retrieves only active contacts.
 * @returns {Promise<Object>} Promise resolving to the organization's contacts.
 * @throws {Error} If the `id` parameter is missing or retrieval fails.
 */
export const getContacts = async (id, activeOnly) => {
  validateRequiredParams({ id });
  return apiGet(`${baseUrl}getcontacts`, { id, activeOnly });
};

/**
 * Retrieves available third-party associates for an organization.
 * @param {string} organizationId - The unique identifier of the organization.
 * @returns {Promise<Object>} Promise resolving to available third-party associates.
 * @throws {Error} If the `organizationId` parameter is missing or retrieval fails.
 */
export const getAvailableThirdPartyAssociates = async (organizationId) => {
  validateRequiredParams({ organizationId });
  return apiGet(`${baseUrl}getavailablethirdpartyassociates`, { id: organizationId });
};

/**
 * Adds a third-party associate to an organization.
 * @param {string} organizationId - The unique identifier of the organization.
 * @param {string} thirdPartyAssociateId - The unique identifier of the third-party associate.
 * @returns {Promise<Object>} Promise resolving to the response data.
 * @throws {Error} If required parameters are missing or the operation fails.
 */
export const addThirdPartyAssociate = async (organizationId, thirdPartyAssociateId) => {
  validateRequiredParams({ organizationId, thirdPartyAssociateId });
  return apiPost(`${baseUrl}addthirdpartyassociate?id=${encodeURIComponent(organizationId)}&tpaId=${encodeURIComponent(thirdPartyAssociateId)}`);
};

/**
 * Deletes a third-party associate from an organization.
 * @param {string} organizationId - The unique identifier of the organization.
 * @param {string} thirdPartyAssociateId - The unique identifier of the third-party associate.
 * @returns {Promise<Object>} Promise resolving to the response data.
 * @throws {Error} If required parameters are missing or the operation fails.
 */
export const deleteThirdPartyAssociate = async (organizationId, thirdPartyAssociateId) => {
  validateRequiredParams({ organizationId, thirdPartyAssociateId });
  return apiPost(`${baseUrl}deletethirdpartyassociate?id=${encodeURIComponent(organizationId)}&tpaId=${encodeURIComponent(thirdPartyAssociateId)}`);
};

